/**
 * DocumentComparisonPanel - Sticky side panel for comparing external CMS
 * documents against internal Finch blobs.
 *
 * Visual states: idle | uploading | processing | completed | failed
 */

import {
  ArrowDownTrayIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useMemo } from 'react';
import { useDropzone } from 'react-dropzone';

import {
  useLazyGetDownloadMissingExternallyQuery,
  useLazyGetDownloadMissingInternallyQuery,
} from '@/services/api/documentComparisonService';
import type { ChangedDocumentEntity } from '@/services/api/generated/crossMatterChangesService';

import {
  type ComparisonPhase,
  useDocumentComparison,
} from '../hooks/useDocumentComparison';
import { useTransferAction } from '../utils/useTransferAction';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PROCESSING_STEPS: { key: string; label: string }[] = [
  { key: 'extracting', label: 'Extracting' },
  { key: 'hashing', label: 'Hashing' },
  { key: 'comparing', label: 'Comparing' },
  { key: 'building_results', label: 'Building' },
  { key: 'completed', label: 'Done' },
];

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

const IdleState = ({
  getRootProps,
  getInputProps,
  isDragActive,
}: {
  getRootProps: ReturnType<typeof useDropzone>['getRootProps'];
  getInputProps: ReturnType<typeof useDropzone>['getInputProps'];
  isDragActive: boolean;
}) => {
  const zoneClass = isDragActive
    ? 'border-blue-400 bg-blue-50/50'
    : 'border-gray-300 hover:border-gray-400';

  return (
    <div
      {...getRootProps()}
      className={`cursor-pointer rounded-lg border-2 border-dashed p-8 text-center transition-colors ${zoneClass}`}
    >
      <input {...getInputProps()} />
      <CloudArrowUpIcon className="mx-auto h-10 w-10 text-gray-400" />
      <p className="mt-3 text-sm font-semibold text-gray-700">
        Compare Documents
      </p>
      <p className="mt-1 text-xs text-gray-500">
        Drop a zip from your external CMS to compare against Finch documents
      </p>
    </div>
  );
}

const UploadingState = ({ fileName }: { fileName: string | null }) => {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-500" />
        <span className="text-sm font-medium text-gray-700">Uploading...</span>
      </div>
      {fileName && (
        <p className="truncate text-xs text-gray-500">{fileName}</p>
      )}
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-blue-100">
        <div
          className="h-full animate-pulse rounded-full bg-blue-500"
          style={{ width: '60%' }}
        />
      </div>
    </div>
  );
}

const ProcessingState = ({
  currentStatus,
  processedCount,
  totalFiles,
}: {
  currentStatus: string;
  processedCount: number;
  totalFiles: number;
}) => {
  const currentIdx = PROCESSING_STEPS.findIndex(
    (s) => s.key === currentStatus,
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-500" />
        <span className="text-sm font-medium text-gray-700">
          Processing comparison...
        </span>
      </div>

      {/* Step indicator */}
      <div className="flex items-center justify-between">
        {PROCESSING_STEPS.map((step, idx) => {
          const isActive = idx === currentIdx;
          const isDone = idx < currentIdx;
          const dotClass = isDone
            ? 'bg-blue-500'
            : isActive
              ? 'bg-blue-500 animate-pulse'
              : 'bg-gray-200';
          const labelClass = isActive
            ? 'font-medium text-blue-600'
            : 'text-gray-400';

          return (
            <div key={step.key} className="flex flex-col items-center gap-1">
              <div
                className={`h-2.5 w-2.5 rounded-full transition-colors ${dotClass}`}
              />
              <span className={`text-[10px] leading-tight ${labelClass}`}>
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      {totalFiles > 0 && (
        <p className="text-center text-xs text-gray-500">
          {processedCount} / {totalFiles} files
        </p>
      )}
    </div>
  );
}

const CompletedState = ({
  comparison,
  matterId,
  documentEventIds,
}: {
  comparison: {
    id: string;
    total_external_files: number;
    missing_internally_count: number;
    missing_externally_count: number;
    missing_internally_zip_key: string | null;
    missing_externally_zip_key: string | null;
  };
  matterId: string;
  documentEventIds: string[];
}) => {
  const matched =
    comparison.total_external_files - comparison.missing_internally_count;

  const [fetchMissingInternally] = useLazyGetDownloadMissingInternallyQuery();
  const [fetchMissingExternally] = useLazyGetDownloadMissingExternallyQuery();

  const {
    handleTransfer,
    handleSkip,
    isLoading: isTransferring,
    status: transferStatus,
  } = useTransferAction(documentEventIds);

  const handleDownloadMissingInternally = useCallback(async () => {
    const result = await fetchMissingInternally({
      matterId,
      comparisonId: comparison.id,
    }).unwrap();
    window.open(result.download_url, '_blank');
  }, [fetchMissingInternally, matterId, comparison.id]);

  const handleDownloadMissingExternally = useCallback(async () => {
    const result = await fetchMissingExternally({
      matterId,
      comparisonId: comparison.id,
    }).unwrap();
    window.open(result.download_url, '_blank');
  }, [fetchMissingExternally, matterId, comparison.id]);

  return (
    <div className="space-y-4">
      <p className="text-sm font-semibold text-gray-700">
        Comparison Complete
      </p>

      {/* Stats */}
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="h-5 w-5 text-green-500" />
          <span className="text-sm text-gray-700">{matched} matched</span>
        </div>
        <div className="flex items-center gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
          <span className="text-sm text-gray-700">
            {comparison.missing_internally_count} missing in Finch
          </span>
        </div>
        <div className="flex items-center gap-2">
          <InformationCircleIcon className="h-5 w-5 text-blue-500" />
          <span className="text-sm text-gray-700">
            {comparison.missing_externally_count} missing externally
          </span>
        </div>
      </div>

      {/* Download buttons */}
      <div className="flex flex-col gap-2">
        {comparison.missing_internally_zip_key && (
          <button
            type="button"
            onClick={handleDownloadMissingInternally}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            Download missing in Finch
          </button>
        )}
        {comparison.missing_externally_zip_key && (
          <button
            type="button"
            onClick={handleDownloadMissingExternally}
            className="inline-flex items-center justify-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
          >
            <ArrowDownTrayIcon className="h-3.5 w-3.5" />
            Download missing externally
          </button>
        )}
      </div>

      {/* Divider */}
      {documentEventIds.length > 0 && <hr className="border-gray-200" />}

      {/* Bulk actions */}
      {documentEventIds.length > 0 && transferStatus === 'idle' && (
        <div className="flex flex-col gap-2">
          <button
            type="button"
            onClick={handleTransfer}
            disabled={isTransferring}
            className="w-full rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-blue-700 disabled:opacity-50"
          >
            Transfer All Documents
          </button>
          <button
            type="button"
            onClick={handleSkip}
            disabled={isTransferring}
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:opacity-50"
          >
            Skip All Documents
          </button>
        </div>
      )}

      {transferStatus === 'transferred' && (
        <p className="text-center text-sm font-medium text-green-600">
          All documents marked as transferred
        </p>
      )}
      {transferStatus === 'skipped' && (
        <p className="text-center text-sm font-medium text-gray-500">
          All documents marked as skipped
        </p>
      )}
    </div>
  );
}

const FailedState = ({
  errorMessage,
  onRetry,
}: {
  errorMessage: string | null;
  onRetry: () => void;
}) => {
  return (
    <div className="space-y-3">
      <div className="rounded-md bg-red-50 p-3">
        <div className="flex items-start gap-2">
          <XCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-red-400" />
          <p className="text-sm text-red-700">
            {errorMessage || 'Comparison failed'}
          </p>
        </div>
      </div>
      <button
        type="button"
        onClick={onRetry}
        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
      >
        Try Again
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

interface DocumentComparisonPanelProps {
  matterId: string;
  documents: ChangedDocumentEntity[];
}

export const DocumentComparisonPanel = ({
  matterId,
  documents,
}: DocumentComparisonPanelProps) => {
  const { phase, comparison, fileName, error, handleFileDrop, reset } =
    useDocumentComparison(matterId);

  const documentEventIds = useMemo(
    () => documents.flatMap((doc) => doc.events.map((e) => e.id)),
    [documents],
  );

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      if (file) handleFileDrop(file);
    },
    [handleFileDrop],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/zip': ['.zip'] },
    maxFiles: 1,
    disabled: phase !== 'idle',
  });

  const renderContent = (currentPhase: ComparisonPhase) => {
    switch (currentPhase) {
      case 'idle':
        return (
          <IdleState
            getRootProps={getRootProps}
            getInputProps={getInputProps}
            isDragActive={isDragActive}
          />
        );
      case 'uploading':
        return <UploadingState fileName={fileName} />;
      case 'processing':
        return (
          <ProcessingState
            currentStatus={comparison?.status ?? 'pending'}
            processedCount={comparison?.processed_count ?? 0}
            totalFiles={comparison?.total_external_files ?? 0}
          />
        );
      case 'completed':
        return comparison ? (
          <CompletedState
            comparison={comparison}
            matterId={matterId}
            documentEventIds={documentEventIds}
          />
        ) : null;
      case 'failed':
        return <FailedState errorMessage={error} onRetry={reset} />;
      default:
        return null;
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
      {renderContent(phase)}

      {/* Reset link for completed state */}
      {phase === 'completed' && (
        <button
          type="button"
          onClick={reset}
          className="mt-4 w-full text-center text-xs text-blue-600 transition-colors hover:text-blue-800"
        >
          New Comparison
        </button>
      )}
    </div>
  );
};
